import React from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useFeatureAnalytics } from '../../hooks/useFeatureAnalytics';
import FilterBar from '../../components/feature-analytics/FilterBar';
import KPICard from '../../components/founder-dashboard/KPICard';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import DistributionChart from '../../components/founder-dashboard/DistributionChart';
import DataTable from '../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import { CalendarIcon } from '@heroicons/react/24/outline';

export default function EventsAnalyticsPage() {
  const {
    data, loading, error, showError, lastUpdated, fetchData,
    organizationId, campusId, groupId, handleFilterChange
  } = useFeatureAnalytics(founderDashboardAPI.getEventsAnalytics);

  if (showError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const { kpis = {}, trends = [], distribution = {}, table = [] } = data || {};

  const columns = [
    { key: 'name', header: 'Group Name', sortable: true },
    { key: 'organizationName', header: 'Organization', sortable: true },
    {
      key: 'events30d',
      header: 'Events (30d)',
      sortable: true,
      render: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'completionRate',
      header: 'Completion Rate',
      sortable: true,
      render: (value) => typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : 'N/A'
    },
    {
      key: 'rsvpRate',
      header: 'RSVP Rate',
      sortable: true,
      render: (value) => typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : 'N/A'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarIcon className="h-6 w-6 text-purple-600" />
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Events Analytics</h2>
            <p className="text-slate-600 text-sm">Event creation, completion, and RSVP patterns</p>
          </div>
        </div>
        <RefreshIndicator lastUpdated={lastUpdated} onRefresh={fetchData} loading={loading} />
      </div>

      <FilterBar
        organizationId={organizationId}
        campusId={campusId}
        groupId={groupId}
        onFilterChange={handleFilterChange}
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard
          title="Adoption Rate"
          value={kpis.adoption || 0}
          format="percent"
          loading={loading && !data}
        />
        <KPICard
          title="Events per Group"
          value={kpis.eventsPerGroup || 0}
          format="decimal"
          subtitle="(30 days)"
          loading={loading && !data}
        />
        <KPICard
          title="RSVP Rate"
          value={kpis.rsvpResponseRate || 0}
          format="percent"
          loading={loading && !data}
        />
        <KPICard
          title="Completion Rate"
          value={kpis.completionRate || 0}
          format="percent"
          loading={loading && !data}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Weekly Event Activity</h3>
          <TrendChart
            data={trends}
            lines={[
              { key: 'events', name: 'Total Events', color: '#8B5CF6' },
              { key: 'completed', name: 'Completed', color: '#22C55E' },
              { key: 'cancelled', name: 'Cancelled', color: '#EF4444' }
            ]}
            type="area"
            height={250}
          />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">RSVP Status Distribution</h3>
          <DistributionChart
            data={distribution.rsvpStatus || {}}
            showPercentage={true}
            height={250}
          />
        </div>
      </div>

      {/* Event Types */}
      {distribution.eventTypes && Object.keys(distribution.eventTypes).length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-8">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Event Types</h3>
          <DistributionChart
            data={distribution.eventTypes}
            showPercentage={true}
            height={200}
          />
        </div>
      )}

      {/* Most Active Groups Table */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Most Active Event Groups</h3>
        <DataTable
          data={table}
          columns={columns}
          loading={loading && !data}
          emptyMessage="No event activity found"
        />
      </div>
    </div>
  );
}
